import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SocialProofCard from "./SocialProofCard";
import casinoService from "../../../services/casinoService";
import activityService from "../../../services/activityService";
import { useAuth } from "../../../contexts/AuthContext";
import { navigateToCBGame } from "../../../services/gameNavigationService";
import logger from "../../utils/logger";
import {
  generateInitialFeed,
  generateLiveActivity,
  FEED_MAX_LENGTH,
} from "../../utils/socialProofGenerator";
import {
  loadShownSelfTxIds,
  recordShownSelfTxId,
  pickNextTickerRow,
} from "../../utils/activityStreams";

// New activity arrives at a randomised gap so the feed feels organic.
const NEW_ACTIVITY_MIN_MS = 8_000;
const NEW_ACTIVITY_MAX_MS = 15_000;

// During quiet gaps between new activities we still need to refresh
// relative-time labels ("8s ago" → "15s ago" → "1m ago") so cards don't
// look frozen. A 10s refresh keeps labels feeling live without needless
// re-renders.
const TIME_REFRESH_MS = 10_000;

// ─── Hybrid stream tunables (see docs/features/20-…) ──────────────────────
// Poll the public backbone every 30s. The endpoint is cache-friendly
// (max-age=10s) and the ring buffer only changes when the sampler ticks.
const BACKBONE_POLL_MS = 30_000;
// Cap the local backbone candidate pool so a long-lived lobby session
// can't accumulate dozens of stale rows. FIFO trimmed to this length.
const BACKBONE_POOL_MAX = 50;
// ~1 in 5 ticker frames picks a backbone row instead of generating
// fake, IF a fresh non-adjacent backbone candidate exists.
const BACKBONE_PICK_PROBABILITY = 0.22;
// Randomised delay before the headline YOU row injects on lobby mount.
// 1.2–1.8s feels like the feed kept moving and your row landed
// naturally, not like the lobby greeted you.
const SELF_DELAYED_INJECT_MIN_MS = 1200;
const SELF_DELAYED_INJECT_MAX_MS = 1800;
// After a YOU row appears, suppress further YOU rows for this long.
// The mount-time inject bypasses this — see effect below.
const SELF_COOLDOWN_MS = 90_000;
// While in lobby with queued YOU rows, drain one every N ms after the
// cooldown clears.
const SELF_DRAIN_INTERVAL_MS = 90_000;
// Per-entry TTL for the shown-self-tx-id localStorage map. After this
// long, a previously-shown tx becomes eligible to show again — but in
// practice the 5-minute freshness window on the backend means a 10-min
// TTL effectively means "never re-show in this session".
const SHOWN_SELF_TX_TTL_MS = 10 * 60_000;
// YOU rows inject at slot 1 (second from top), not slot 0 — the spec is
// explicit: it should feel like the feed kept moving and your row
// landed naturally.
const SELF_INJECT_SLOT = 1;

// Preload an image and resolve when it's in the browser cache (or when
// load fails). Used to keep the slide-in animation from showing a
// thumbnail skeleton — by the time we commit the new card to state and
// trigger the 350ms enter animation, the <img> renders from cache and
// no skeleton flash occurs. Capped at 3s so a slow/broken image doesn't
// stall the live feed indefinitely; if the timer fires first we commit
// anyway and the SocialProofCard's own skeleton handles the gap.
const preloadImage = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve();
      return;
    }
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const timer = setTimeout(finish, 3000);
    const img = new Image();
    img.onload = () => {
      clearTimeout(timer);
      finish();
    };
    img.onerror = () => {
      clearTimeout(timer);
      finish();
    };
    img.src = src;
  });

// Insert a row at the requested slot, capped to the feed's max length.
// If slot is past the feed length (early in lifecycle when fewer than
// SELF_INJECT_SLOT rows exist) we fall back to prepending at the front
// so the row isn't lost entirely. The oldest row drops off the tail to
// keep the feed at FEED_MAX_LENGTH.
const insertAtSlot = (feed, row, slot) => {
  const idx = Math.min(Math.max(0, slot), feed.length);
  const next = [...feed.slice(0, idx), row, ...feed.slice(idx)];
  return next.slice(0, FEED_MAX_LENGTH);
};

/**
 * Recent Activity feed. Renders a small live ticker of pseudonymous
 * win/loss cards that auto-advances on its own — no manual navigation,
 * no auto-rotating carousel. New activities prepend at the front and
 * the oldest is dropped, so the feed always shows the freshest events.
 *
 * Three converging streams (see docs/features/20-…):
 *   1. Fake generator (existing, dominant supply).
 *   2. Public backbone — real masked wins polled every 30s.
 *   3. YOU — current user's own wins, injected at slot 1 with orange
 *      accent + YOU badge, cooldown-limited, persisted across refreshes.
 *
 * Pulls from a real game pool when the backend is up; falls back to a
 * curated list of well-known titles when offline. Either way the
 * generator output is identical in shape, so the section behaves the
 * same in dev and prod.
 */
const SocialProofSection = ({ title = "Recent Activity", className = "" }) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [activities, setActivities] = useState([]);
  const [realGames, setRealGames] = useState(null);
  const [loading, setLoading] = useState(true);
  // _tick is intentionally unused — bumping it forces a re-render so the
  // formatted relative timestamps refresh during quiet gaps.
  const [, setTick] = useState(0);

  const newActivityTimeoutRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  // Keep a live mirror of `activities` so the ticker setTimeout can read
  // the current feed without subscribing to it as an effect dependency
  // (which would tear down and rebuild the timer on every prepend).
  const activitiesRef = useRef([]);
  useEffect(() => {
    activitiesRef.current = activities;
  }, [activities]);

  // ─── Backbone stream refs ─────────────────────────────────────────────
  // Pool of unseen backbone candidates (FIFO, capped at BACKBONE_POOL_MAX).
  const backbonePoolRef = useRef([]);
  // Every backbone id we've ever ingested this mount — used to dedupe
  // poll responses so the same row doesn't enter the pool twice.
  const backboneSeenIdsRef = useRef(new Set());
  // Backbone ids already injected into the feed — pickNextTickerRow uses
  // this to avoid re-picking a row that's already been shown.
  const backboneConsumedIdsRef = useRef(new Set());
  const backbonePollIntervalRef = useRef(null);

  // ─── YOU stream refs ──────────────────────────────────────────────────
  // Queue of YOU activities not yet injected (drained one per cooldown).
  const selfQueueRef = useRef([]);
  // Most recent timestamp (ms) at which a YOU row was injected. Used by
  // the drain interval to enforce SELF_COOLDOWN_MS between YOU rows.
  const selfLastShownAtRef = useRef(0);
  // Pending delayed-inject timer for the mount-fetch headline row.
  const selfInjectTimeoutRef = useRef(null);
  // Drain interval that pops queued YOU rows after cooldown expires.
  const selfDrainIntervalRef = useRef(null);

  // ─── Fetch real games once on mount ─────────────────────────────────
  useEffect(() => {
    let mounted = true;
    const fetchGames = async () => {
      try {
        const response = await casinoService.getNewGames({ limit: 50 });
        if (!mounted) return;
        const games = Array.isArray(response?.games) ? response.games : [];
        setRealGames(games.length > 0 ? games : null);
      } catch (err) {
        logger.warn(
          "SocialProofSection: getNewGames failed, using offline fallback",
          err,
        );
        if (mounted) setRealGames(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchGames();
    return () => {
      mounted = false;
    };
  }, []);

  // ─── Build the initial seed once we know which game pool to use ─────
  useEffect(() => {
    if (loading) return;
    setActivities(generateInitialFeed(realGames, FEED_MAX_LENGTH));
  }, [loading, realGames]);

  // ─── Live ticker: prepend a fresh activity every 8-15s ──────────────
  // Runs once after the initial fetch completes. The setTimeout callback
  // reads the current state via the functional setActivities form, so it
  // always sees the seeded feed by the time the first tick fires.
  //
  // pickNextTickerRow merges fake + backbone supply. Most ticks still
  // produce a fake row; ~22% pick a fresh backbone row instead.
  useEffect(() => {
    if (loading) return;

    const scheduleNext = () => {
      const delay =
        NEW_ACTIVITY_MIN_MS +
        Math.random() * (NEW_ACTIVITY_MAX_MS - NEW_ACTIVITY_MIN_MS);
      newActivityTimeoutRef.current = setTimeout(async () => {
        // Generate against the current feed via the ref so dedupe inside
        // the generator sees the latest items. Preload the thumbnail
        // BEFORE committing — once setActivities fires, the new card
        // mounts with the 350ms slide-in animation, and we want the
        // thumbnail in cache by then so the SocialProofCard skeleton
        // never flashes mid-animation.
        const currentFeed = activitiesRef.current;
        const lastShown = currentFeed[0];
        const fresh = pickNextTickerRow({
          backbonePool: backbonePoolRef.current,
          consumedBackboneIds: backboneConsumedIdsRef.current,
          lastShown,
          fakeGenerator: generateLiveActivity,
          currentFeed,
          realGames,
          backboneProbability: BACKBONE_PICK_PROBABILITY,
        });
        // If we picked a backbone row, mark its id consumed so the
        // picker won't reuse it on future ticks.
        if (fresh?.source === "real" && fresh.id) {
          backboneConsumedIdsRef.current.add(fresh.id);
        }
        const thumbSrc = fresh?.game?.thumbnail || fresh?.game?.image;
        await preloadImage(thumbSrc);
        setActivities((prev) => [fresh, ...prev].slice(0, FEED_MAX_LENGTH));
        scheduleNext();
      }, delay);
    };

    scheduleNext();

    return () => {
      if (newActivityTimeoutRef.current) {
        clearTimeout(newActivityTimeoutRef.current);
      }
    };
  }, [loading, realGames]);

  // ─── Backbone poll: every 30s, fetch /activity/public ───────────────
  // Dedupes by id against backboneSeenIdsRef so the pool only grows
  // when a new sampled row appears server-side. Pool is FIFO-capped at
  // BACKBONE_POOL_MAX. Errors are swallowed by activityService and
  // surface as an empty array, so the fake feed keeps running on any
  // backend hiccup.
  useEffect(() => {
    if (loading) return;

    let cancelled = false;

    const pollBackbone = async () => {
      const rows = await activityService.getPublicActivity();
      if (cancelled || !Array.isArray(rows) || rows.length === 0) return;

      const seen = backboneSeenIdsRef.current;
      const fresh = rows.filter((row) => row?.id && !seen.has(row.id));
      if (fresh.length === 0) return;

      for (const row of fresh) seen.add(row.id);
      // Newer rows live at the front of the pool so the picker sees the
      // freshest candidate first (rows.find returns the first match).
      const merged = [...fresh, ...backbonePoolRef.current].slice(
        0,
        BACKBONE_POOL_MAX,
      );
      backbonePoolRef.current = merged;
    };

    // Initial poll immediately so the pool can prime before the next
    // ticker frame, then settle into the 30s cadence.
    pollBackbone();
    backbonePollIntervalRef.current = setInterval(
      pollBackbone,
      BACKBONE_POLL_MS,
    );

    return () => {
      cancelled = true;
      if (backbonePollIntervalRef.current) {
        clearInterval(backbonePollIntervalRef.current);
      }
    };
  }, [loading]);

  // ─── YOU stream: mount fetch + delayed inject + drain ───────────────
  // On mount, if the user is logged in, fetch their recent wins. Filter
  // against the localStorage shown-set, pick the biggest unseen as the
  // headline (delayed inject 1.2–1.8s into slot 1), queue the rest.
  // While in the lobby, drain the queue every 90s, one row at a time,
  // respecting the cooldown.
  useEffect(() => {
    if (loading) return;
    if (!isLoggedIn) return;

    let cancelled = false;

    const injectSelfRow = async (row) => {
      if (cancelled || !row) return;
      const thumbSrc = row?.game?.thumbnail || row?.game?.image;
      await preloadImage(thumbSrc);
      if (cancelled) return;
      setActivities((prev) => insertAtSlot(prev, row, SELF_INJECT_SLOT));
      selfLastShownAtRef.current = Date.now();
      recordShownSelfTxId(row.id || row.transaction_id);
    };

    const runMountFetch = async () => {
      const rows = await activityService.getMyRecentActivity();
      if (cancelled || !Array.isArray(rows) || rows.length === 0) return;

      const shown = loadShownSelfTxIds(SHOWN_SELF_TX_TTL_MS);
      const unseen = rows.filter((row) => {
        const id = row?.id || row?.transaction_id;
        return id && !shown.has(id);
      });
      if (unseen.length === 0) return;

      // Mark each row with isMe + source so the picker / card render
      // the orange treatment and the row never gets misclassified by
      // the no-adjacency logic.
      const tagged = unseen.map((row) => ({ ...row, isMe: true, source: "me" }));
      // Headline = biggest unseen. The endpoint returns rows sorted by
      // amount DESC already, but sort defensively so a future contract
      // change doesn't silently demote the headline.
      tagged.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
      const [headline, ...rest] = tagged;
      selfQueueRef.current = rest;

      const delay =
        SELF_DELAYED_INJECT_MIN_MS +
        Math.random() *
          (SELF_DELAYED_INJECT_MAX_MS - SELF_DELAYED_INJECT_MIN_MS);
      selfInjectTimeoutRef.current = setTimeout(() => {
        injectSelfRow(headline);
      }, delay);
    };

    runMountFetch();

    // Drain queued YOU wins one at a time once the cooldown clears.
    // Mount-time inject bypasses cooldown (handled by runMountFetch).
    selfDrainIntervalRef.current = setInterval(() => {
      if (cancelled) return;
      if (selfQueueRef.current.length === 0) return;
      const cooldownClearAt =
        selfLastShownAtRef.current + SELF_COOLDOWN_MS;
      if (Date.now() < cooldownClearAt) return;
      const next = selfQueueRef.current.shift();
      injectSelfRow(next);
    }, SELF_DRAIN_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (selfInjectTimeoutRef.current) {
        clearTimeout(selfInjectTimeoutRef.current);
      }
      if (selfDrainIntervalRef.current) {
        clearInterval(selfDrainIntervalRef.current);
      }
    };
  }, [loading, isLoggedIn]);

  // ─── Refresh tick: re-render every 10s so relative times update ─────
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      setTick((t) => t + 1);
    }, TIME_REFRESH_MS);
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Cards are clickable only when we have real game data — fallback
  // games don't link to a real launch URL.
  const handleCardClick = (activity) => {
    if (!realGames || !activity?.game?.game_code) return;
    navigateToCBGame(navigate, activity.game.game_code);
  };

  // ─── Loading state ──────────────────────────────────────────────────
  if (loading) {
    return (
      <section className={className || "mb-8"}>
        {title && title.trim() !== "" && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white leading-tight">
              {title}
            </h2>
          </div>
        )}

        {/* Loading skeleton — mirrors the SocialProofCard layout so the
            transition into real content doesn't reflow. */}
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: FEED_MAX_LENGTH }).map((_, index) => (
            <div
              key={index}
              className="bg-card border border-border/80 rounded-xl animate-pulse p-4 flex-shrink-0 w-full sm:w-1/2 md:w-2/5 lg:w-1/3 xl:w-1/4"
            >
              <div className="flex items-start gap-3">
                <div className="w-14 h-14 bg-card rounded-lg flex-shrink-0"></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="h-4 bg-card rounded flex-1 max-w-[60%]"></div>
                    <div className="h-4 w-12 bg-card rounded-md flex-shrink-0"></div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="h-3.5 w-20 bg-card rounded"></div>
                    <div className="h-3 w-px bg-card"></div>
                    <div className="h-3 w-12 bg-card rounded"></div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <div className="h-6 w-20 bg-card rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // ─── Live feed ──────────────────────────────────────────────────────
  return (
    <section className={`${className || "mb-8"} relative`}>
      {title && title.trim() !== "" && (
        <div className="mb-6">
          {/* Live Games kicker — uppercase gold to match the rest of the
              lobby's section kickers (Slots, Providers). Pulsing dot stays:
              it signals the feed is auto-updating, which justifies the
              "live" claim. */}
          <div className="flex items-center gap-2 mb-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            <p className="text-[11px] md:text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              Live Games
            </p>
          </div>
          <h2 className="text-2xl font-bold text-white leading-tight">
            {title}
          </h2>
        </div>
      )}

      {/* Feed row. overflow-hidden clips cards that don't fit at smaller
          breakpoints (e.g. on mobile only the newest 1 is visible) — this
          is intentional: the live ticker always shows the freshest items
          at the front. */}
      <div className="flex gap-4 overflow-hidden">
        {activities.map((activity) => (
          <div
            key={activity.id}
            className="flex-shrink-0 w-full sm:w-1/2 md:w-2/5 lg:w-1/3 xl:w-1/4 social-proof-card-enter"
          >
            <SocialProofCard
              game={activity.game}
              user={activity.user}
              amount={activity.amount}
              currency={activity.currency}
              isWin={activity.isWin}
              timestamp={activity.timestamp}
              isMe={activity.isMe === true}
              onClick={() => handleCardClick(activity)}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export default SocialProofSection;
